import {
  combineUnits,
  formatAmount,
  getUnitOptions,
  getUnitOrFallback,
  stringIsValidNumber,
  stringToNumber,
  toAbsoluteAmount,
  toRelativeAmount,
  toRelativePrice,
  unitToOption,
} from "@/lib/measurement";
import { isRecipeLocked } from "@/lib/recipe-utilities";
import useLocalStorageState from "@/lib/useLocalStorageState";
import { classNames } from "@/lib/util";
import {
  Exact,
  IngredientComponent,
  RecipeQuery,
  RecipeUpdateInput,
  RecipeWhereUniqueInput,
  UpdateRecipeMutation,
  useUpdateRecipeIngredientMutation,
} from "@/queries/autogenerate/graphql";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import { UseMutationExecute } from "urql";
import { IngredientNote } from "../Commons";
import DialogComponent from "../Dialog";
import { SortableItem } from "../DragNDrop";
import ErrorMessage from "../ErrorMessage";
import IngredientsGridItem from "../IngredientsGridItem";
import LinkImage from "../LinkImage";
import { CurrentUser } from "../Page";
import { InventoryGrid } from "../styles/Layout";
import Tooltip from "../styles/Tooltip";
import DragIndicatorIcon from "../svg/DragIndicatorIcon";

interface Props {
  recipe: NonNullable<RecipeQuery["recipe"]>;
  ingredient: NonNullable<RecipeQuery["recipe"]>["recipeIngredients"][number];
  index: number;
  updateRecipe: UseMutationExecute<
    UpdateRecipeMutation,
    Exact<{
      where: RecipeWhereUniqueInput;
      data: RecipeUpdateInput;
    }>
  >;
  user: CurrentUser;
}

const RecipesGridIngredient: React.FC<Props> = ({
  recipe,
  ingredient: {
    id,
    position,
    amount,
    preparation,
    unit: defaultUnit,
    recipeSection,
    ingredient: {
      id: ingredientId,
      name,
      note,
      link,
      units: customUnits,
      measurement,
      density,
      prices,
    },
  },
  index,
  updateRecipe,
  user,
}) => {
  const [deleted, setDeleted] = useState(false);

  const locked = isRecipeLocked(recipe, user);

  const [amountNumberFormat] = useLocalStorageState(
    "amountNumberFormat",
    "decimal"
  );

  const units = combineUnits({ measurement, units: customUnits, density });
  const unit = getUnitOrFallback(units, defaultUnit);
  const relativeAmount = unit
    ? formatAmount(
        amountNumberFormat,
        toRelativeAmount(amount, unit, density || undefined, recipe.servings)
      )
    : 0;

  const [isEditingIngredient, setEditingIngredient] = useState(false);
  const [editedIngredientAmount, setEditedIngredientAmount] = useState<
    string | number
  >(relativeAmount);
  const [editedIngredientUnit, setEditedIngredientUnit] = useState(unit);
  const [editedIngredientPreparation, setEditedIngredientPreparation] =
    useState(preparation || "");

  const [editDialog, setEditDialog] = useState(false);
  const open = () => setEditDialog(true);
  const close = () => setEditDialog(false);

  const [{ fetching }, updateRecipeIngredient] =
    useUpdateRecipeIngredientMutation();

  if (!unit) {
    console.error(
      `Ingredient ${ingredientId} in recipe ${recipe.id} has no unit`
    );
    return null;
  }

  const cost = toRelativePrice(
    prices[0],
    { amount: recipe.servings * amount },
    {
      units,
      measurement,
      density,
    }
  );

  const somethingHasLinkOrPrice = recipe.recipeIngredients
    .filter((ri) =>
      recipeSection && recipeSection.id !== "sectionLess"
        ? ri.recipeSection?.id === recipeSection.id
        : !ri.recipeSection
    )
    .some((ri) => ri.ingredient.link || ri.ingredient.prices.length);

  return deleted ? null : (
    <SortableItem
      id={id}
      index={index}
      column={recipeSection ? recipeSection.id : "sectionLess"}
    >
      {({ ref, handleRef, isDragging }) => (
        <li className="group/ingredient group my-2 space-x-1" ref={ref}>
          {locked ? null : (
            <div
              className="inline-block"
              title="Sort Ingredients"
              ref={handleRef}
            >
              <DragIndicatorIcon className="w-5 opacity-0 transition group-hover:opacity-100" />
            </div>
          )}
          {link || prices.length ? (
            <LinkImage link={link || undefined} />
          ) : somethingHasLinkOrPrice ? (
            <div className="inline-block h-4 w-4" />
          ) : null}
          <button className="visible" onClick={() => open()}>
            {name}
            {note ? <IngredientNote note={note} /> : null}
          </button>
          <DialogComponent
            isOpen={editDialog}
            onClose={close}
            aria-label="Edit ingredient"
          >
            <h2>Edit {name}</h2>
            <div className="mx-0 my-5 max-w-xl rounded-md bg-orange-100 p-2.5 shadow">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon
                    className="w-3 text-orange-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-2 flex-1 md:flex md:justify-between">
                  <p className="text-xdl text-orange-900">
                    Note: editing an ingredient will change it for all recipes
                    that use it, not just this one.
                  </p>
                </div>
              </div>
            </div>
            <Link
              prefetch={false}
              href={`/ingredients#${ingredientId}`}
              className="mb-8 mt-2 block text-blue-500 hover:text-blue-700"
            >
              Edit {name} from the ingredients page &rarr;
            </Link>
            <IngredientComponent variables={{ where: { id: ingredientId } }}>
              {({ fetching, error, data }) =>
                error ? (
                  <ErrorMessage error={error} />
                ) : fetching ? (
                  <div className="spinner" />
                ) : data?.ingredient ? (
                  <InventoryGrid itemHeight="90px" itemWidth="400px">
                    <IngredientsGridItem
                      user={user}
                      ingredient={data.ingredient}
                      isFragment={true}
                    />
                  </InventoryGrid>
                ) : (
                  <p>No Data! (361)</p>
                )
              }
            </IngredientComponent>
          </DialogComponent>
          {preparation ? <small>({preparation})</small> : null}
          {relativeAmount && relativeAmount !== "0" ? (
            <small>
              ({relativeAmount}
              {` ${unit.name}`})
            </small>
          ) : null}
          {cost ? (
            <small className="font-normal text-amber-700">
              (${cost.toFixed(2)})
            </small>
          ) : null}
          {isEditingIngredient ? (
            <form
              className="text-sm"
              onSubmit={(e) => {
                e.preventDefault();

                if (!stringIsValidNumber(String(editedIngredientAmount))) {
                  toast.error(
                    `"${editedIngredientAmount}" is not a valid input`
                  );
                  return;
                }
                if (!editedIngredientUnit) {
                  toast.error(`No unit selected`);
                  return;
                }

                setEditingIngredient(false);

                updateRecipeIngredient({
                  where: { id },
                  data: {
                    amount: {
                      set: toAbsoluteAmount(
                        stringToNumber(String(editedIngredientAmount)),
                        editedIngredientUnit,
                        density || undefined,
                        recipe.servings
                      ),
                    },
                    unit: {
                      connect: {
                        id: editedIngredientUnit.id,
                      },
                    },
                    preparation: {
                      set: editedIngredientPreparation || null,
                    },
                  },
                }).then(({ error }) => {
                  if (error) {
                    toast.error(`There was an error: ${error.message}`);
                  }
                });
                // onUpdateRecipeCompleted();
              }}
            >
              <div className="flex items-center gap-1 p-1">
                <div className="relative inline-block">
                  <input
                    type="text"
                    onChange={(e) => setEditedIngredientAmount(e.target.value)}
                    value={editedIngredientAmount ?? ""}
                    className={classNames(
                      "input w-20 rounded border",
                      stringIsValidNumber(String(editedIngredientAmount))
                        ? ""
                        : "border-orange-400 outline-orange-600"
                    )}
                    required={true}
                    autoFocus
                  />
                  {stringIsValidNumber(
                    String(editedIngredientAmount)
                  ) ? null : (
                    <Tooltip
                      content={`"${editedIngredientAmount}" is not a valid input`}
                    >
                      <span>
                        <ExclamationTriangleIcon className="icon icon-orange visible absolute right-0 top-0 mr-3 mt-3.5" />
                      </span>
                    </Tooltip>
                  )}
                </div>
                <Select
                  className="radio-select"
                  classNamePrefix="react-select"
                  defaultValue={unitToOption(unit)}
                  options={getUnitOptions(units, density || undefined)}
                  noOptionsMessage={() => "No Options"}
                  onChange={(data) =>
                    setEditedIngredientUnit(
                      units.find((u) => u.id === data?.value)
                    )
                  }
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "175px",
                      display: "inline-block",
                    }),
                  }}
                />
                <input
                  type="text"
                  onChange={(e) =>
                    setEditedIngredientPreparation(e.target.value)
                  }
                  value={editedIngredientPreparation}
                  placeholder="grated, sliced..."
                  className="input flex-1"
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={fetching}
                >
                  {fetching ? <div className="spinner"></div> : null}
                  <CheckCircleIcon className="w-5 text-green-800" /> Confirm
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setEditingIngredient(false);
                  }}
                >
                  <XCircleIcon className="w-5 text-gray-900" /> Cancel
                </button>
              </div>
            </form>
          ) : locked ? null : (
            <>
              {/* className=btn so the buttons only show on listItem hovered */}
              <Tooltip content="Edit Ingredient">
                <button onClick={() => setEditingIngredient(true)}>
                  <PencilIcon className="icon icon-gray opacity-0 group-hover/ingredient:opacity-100" />
                </button>
              </Tooltip>
              <Tooltip content="Remove Ingredient">
                <button
                  onClick={async () => {
                    setDeleted(true);

                    await updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        recipeIngredients: { delete: [{ id }] },
                      },
                    });
                    // onUpdateRecipeCompleted();
                  }}
                >
                  <TrashIcon className="icon icon-orange opacity-0 group-hover/ingredient:opacity-100" />
                </button>
              </Tooltip>
            </>
          )}
        </li>
      )}
    </SortableItem>
  );
};

export default RecipesGridIngredient;
